import React from 'react';
import { amountToWordsSoles } from '../../utils/amountToWords';
import { formatPrintDate, formatPrintDateOrDash } from '../../utils/dateUtils';

const formatQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

const formatMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function docTypeLabel(client) {
  if (client?.document_type === 'ruc') return 'RUC';
  if (client?.document_type === 'dni') return 'DNI';
  if (client?.document_type) return String(client.document_type).toUpperCase();
  return 'Documento';
}

const SalesContinuousServicePrintDocument = ({ service }) => {
  if (!service) return null;

  const statusTone = service.status === 'open' ? 'open' : 'completed';
  const statusText = service.status === 'open' ? 'Abierto' : 'Cerrado';
  const progress = [...(service.progress || [])].sort((a, b) =>
    String(a.progress_date).localeCompare(String(b.progress_date)),
  );

  return (
    <div className="print-container">
      <h1 className="doc-title">Servicio continuo — Ventas</h1>
      <hr className="doc-title-rule" />

      <div className="info-line">
        <span className="info-label">Código:</span> {service.code}
        <span className="info-sep">·</span>
        <span className="info-label">Estado:</span>{' '}
        <span className={`status-badge status-${statusTone}`}>{statusText}</span>
        <span className="info-sep">·</span>
        <span className="info-label">Inicio:</span> {formatPrintDateOrDash(service.start_date)}
        <span className="info-sep">·</span>
        <span className="info-label">Fin:</span>{' '}
        {service.end_date ? formatPrintDateOrDash(service.end_date) : 'En curso'}
      </div>

      <div className="info-line">
        <span className="info-label">Cliente:</span> {service.client?.name || 'N/A'}
        {service.client?.document_number && (
          <>
            <span className="info-sep">·</span>
            <span className="info-label">{docTypeLabel(service.client)}:</span>{' '}
            {service.client.document_number}
          </>
        )}
      </div>

      {service.description && (
        <div className="info-line">
          <span className="info-label">Descripción:</span> {service.description}
        </div>
      )}

      <div className="items-section">
        <div className="section-title">Avances</div>

        <div className="items-table-wrap">
          <table className="items-table cols-ventas-progress">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Descripción</th>
                <th className="num">Cant.</th>
                <th className="num">P. Unit</th>
                <th className="num">Subtotal</th>
                <th>IGV</th>
              </tr>
            </thead>
            <tbody>
              {progress.length === 0 ? (
                <tr>
                  <td colSpan={6}>Sin avances registrados</td>
                </tr>
              ) : (
                progress.map((row, index) => (
                  <tr key={row.id ?? index}>
                    <td>{formatPrintDate(row.progress_date)}</td>
                    <td>{row.description || '—'}</td>
                    <td className="num">{formatQty(row.quantity)}</td>
                    <td className="num">S/ {formatMoney(row.unit_price)}</td>
                    <td className="subtotal-cell">S/ {formatMoney(row.subtotal)}</td>
                    <td>{row.has_igv ? `${row.tax_rate || 18}%` : 'Exon.'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="print-footer">
          <div className="print-footer-obs" />
          <div className="print-footer-total">
            <div className="print-total-row">
              <span className="print-total-label">Base:</span>
              <span className="print-total-value">S/ {formatMoney(service.base_imponible)}</span>
            </div>
            <div className="print-total-row">
              <span className="print-total-label">IGV:</span>
              <span className="print-total-value">S/ {formatMoney(service.igv_amount)}</span>
            </div>
            <div className="print-total-row print-total-row-final">
              <span className="print-total-label">Total:</span>
              <span className="print-total-value">S/ {formatMoney(service.total_amount)}</span>
            </div>
            <div className="print-totals-words">Son: {amountToWordsSoles(service.total_amount)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesContinuousServicePrintDocument;
